import {
  Tabs as RadixTabs,
  TabsContent as RadixTabsContent,
  TabsList as RadixTabsList,
  TabsTrigger as RadixTabsTrigger,
} from '@radix-ui/react-tabs';
import { sortBy } from 'lodash';
import { forwardRef, type PropsWithChildren, useMemo } from 'react';

import { type ButtonProps, cn, TailwindButton } from '../../../tailwind';
import { RippleRoot } from '../../../tailwind/RippleRoot';

import {
  GeneralFiltersGroupOptions,
  GeneralFiltersNoOptions,
  SelectedCountBadge,
} from './GeneralFilters.components';
import {
  GeneralFiltersContext,
  useGeneralFiltersContext,
} from './GeneralFilters.context';
import { type GeneralFilterGroup, type SelectedGeneralFilters } from './types';

// Base Provider Component
export interface GeneralFiltersProps extends PropsWithChildren {
  filterGroups: GeneralFilterGroup[];
  selectedFilters: SelectedGeneralFilters;
  onSelectedFiltersChange: (selectedFilters: SelectedGeneralFilters) => void;
}

export const GeneralFilters = ({
  children,
  filterGroups,
  selectedFilters,
  onSelectedFiltersChange,
}: GeneralFiltersProps) => {
  const toggleOption = (groupId: string, optionId: string, value: boolean) => {
    const newSelectedFilters = {
      ...selectedFilters,
      [groupId]: {
        ...selectedFilters[groupId],
        [optionId]: value,
      },
    };
    onSelectedFiltersChange(newSelectedFilters);
  };

  const toggleFilterGroup = (
    groupId: string,
    value: boolean,
    optionIds: string[],
  ) => {
    const filterGroup = filterGroups.find((group) => group.id === groupId);
    if (filterGroup) {
      const targetOptionIds = optionIds;
      const newSelectedFilters = {
        ...selectedFilters,
        [groupId]: {
          ...selectedFilters[groupId],
          ...Object.fromEntries(
            targetOptionIds.map((optionId) => [optionId, value]),
          ),
        },
      };
      onSelectedFiltersChange(newSelectedFilters);
    }
  };

  const sortedFilterGroups = useMemo(() => {
    const sortedFilterGroupsByGroupName = sortBy(filterGroups, [
      'translatedDisplayName',
    ]);

    return sortedFilterGroupsByGroupName.map((group) => {
      // Size groups come back from the server already in retail order
      // (S, M, L, XL); only non-size groups need client-side alphabetical sort.
      if (group.id !== 'size_id' && group.id !== 'sizes') {
        group.options = sortBy(group.options, ['translatedDisplayName']);
      }
      return group;
    });
  }, [filterGroups]);

  return (
    <GeneralFiltersContext.Provider
      value={{
        filterGroups: sortedFilterGroups,
        toggleOption,
        toggleFilterGroup,
        setToggledOptions: onSelectedFiltersChange,
        toggledOptions: selectedFilters,
      }}
    >
      {children}
    </GeneralFiltersContext.Provider>
  );
};

// Root Component (Tabs Container)
interface RootProps extends PropsWithChildren {
  className?: string;
  onValueChange?: (value: string) => void;
}

const Root = ({ children, className, onValueChange }: RootProps) => {
  const { filterGroups } = useGeneralFiltersContext();
  return (
    <RadixTabs
      defaultValue={filterGroups[0]?.id ?? ''}
      className={cn(
        'flex flex-row bg-white min-h-0 min-w-0 h-full grow overflow-hidden',
        className,
      )}
      onValueChange={onValueChange}
    >
      {children}
    </RadixTabs>
  );
};

GeneralFilters.Root = Root;

// Sidebar Component
interface SidebarProps extends PropsWithChildren {
  className?: string;
}

const Sidebar = ({ children, className }: SidebarProps) => (
  <RadixTabsList
    className={cn(
      'flex flex-col px-5 py-3 basis-1/3 min-h-0 self-stretch border-e border-e-grey-20 overflow-y-scroll no-scrollbar min-w-64',
      className,
    )}
  >
    {children}
  </RadixTabsList>
);

GeneralFilters.Sidebar = Sidebar;

// Group List Component
interface GroupListProps {
  className?: string;
  children?: React.ReactNode;
}

const GroupList = ({ className, children }: GroupListProps) => {
  return (
    <div className={cn('flex flex-col gap-1 self-stretch', className)}>
      {children}
    </div>
  );
};

GeneralFilters.GroupList = GroupList;

// Custom Trigger Component
interface TriggerProps extends PropsWithChildren {
  value: string;
  className?: string;
}

const Trigger = ({ value, children, className }: TriggerProps) => (
  <RadixTabsTrigger
    value={value}
    className={cn(
      'relative overflow-hidden flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-grey-80 hover:bg-grey-5 data-[state=active]:text-primary data-[state=active]:bg-primary-5',
      className,
    )}
  >
    {children}
    <RippleRoot
      className="bg-primary-40"
      pulseDuration={0.4}
      pulseOpacity={0.5}
    />
  </RadixTabsTrigger>
);

GeneralFilters.Trigger = Trigger;

// Content Container Component
interface ContentProps extends PropsWithChildren {
  className?: string;
}

const Content = ({ children, className }: ContentProps) => (
  <div className={cn('flex flex-col grow', className)}>{children}</div>
);

GeneralFilters.Content = Content;

// Content Panel Component
interface PanelProps extends PropsWithChildren {
  value: string;
  className?: string;
}

const Panel = ({ value, children, className }: PanelProps) => (
  <RadixTabsContent value={value} className={cn('grow', className)}>
    {children}
  </RadixTabsContent>
);

GeneralFilters.Panel = Panel;

// Clear All Button Component
export const GeneralFiltersClearAllButton = forwardRef<
  HTMLButtonElement,
  ButtonProps
>(({ className, ...props }, ref) => {
  const { setToggledOptions } = useGeneralFiltersContext();

  return (
    <TailwindButton
      onClick={() => setToggledOptions({})}
      className={className}
      variant={'outlined'}
      color={'secondary'}
      {...props}
      ref={ref}
    />
  );
});

export const GeneralFiltersView = ({
  filterGroups,
  selectedFilters,
  onSelectedFiltersChange,
  children,
}: GeneralFiltersProps) => (
  <GeneralFilters
    filterGroups={filterGroups}
    selectedFilters={selectedFilters}
    onSelectedFiltersChange={onSelectedFiltersChange}
  >
    <GeneralFilters.Root>{children}</GeneralFilters.Root>
  </GeneralFilters>
);

export const GeneralFiltersContent = () => {
  const { filterGroups, toggledOptions } = useGeneralFiltersContext();
  const hasFilterGroups = filterGroups.length > 0;
  return (
    <GeneralFilters.Root>
      {hasFilterGroups ? (
        <>
          <GeneralFilters.Sidebar>
            <GeneralFilters.GroupList>
              {filterGroups.map((group) => (
                <GeneralFilters.Trigger key={group.id} value={group.id}>
                  {group.translatedDisplayName}
                  <SelectedCountBadge
                    data-testid={`${group.id}-active-count`}
                    count={
                      Object.values(toggledOptions[group.id] || {}).filter(
                        Boolean,
                      ).length
                    }
                  />
                </GeneralFilters.Trigger>
              ))}
            </GeneralFilters.GroupList>
          </GeneralFilters.Sidebar>
          <GeneralFilters.Content>
            {filterGroups.map((group) => (
              <GeneralFiltersGroupOptions key={group.id} filterGroup={group} />
            ))}
          </GeneralFilters.Content>
        </>
      ) : (
        <GeneralFiltersNoOptions />
      )}
    </GeneralFilters.Root>
  );
};
